import IngestionHeader from '../ingestion/top-panel/IngestionHeader';
import IngestionSummary from '../ingestion/ingestion-summary/IngestionSummary';
import IngestionFilter from '../ingestion/filter-panel/IngestionFilter';
import IngestionTile from './ingestion-tile/IngestionTile';
import {ISelectItem, SummaryStats} from '../../shared/types';
import {IngestionListRequest} from '../../api/types/ingestionApi.type';
import {useEffect, useState, useMemo, useRef} from 'react';
import {Box, Typography} from '@mui/material';
import './ViewIngestion.scss';
import {ReactComponent as NoDataIcon} from '../../assets/images/no-data-found.svg';
import ArrLoadingSpinner from '../common/arr-loading-spinner/ArrLoadingSpinner';

import {
  useGetIngestionData,
  useGetIngestionListData,
  useIngestionSummary,
} from '../../hooks/ingestions/useIngestions';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../shared/constants';
import {t} from 'i18next';

function ViewIngestion() {
  const [showAllState, setShowAllState] = useState(false);
  const DEFAULT_PAGE_SIZE = 360;

  useEffect(() => {
    isFirstRender.current = false;
  }, []);
  const [AppliedFilters, setAppliedFilters] = useState<IngestionListRequest>({
    filterBy: 'INPROGRESS',
    sortBy: 'ModifiedDate',
    SortingOrder: 'desc',
    pageSize: 12,
    pageNumber: 1,
    ingestionNameSearchTerm: '',
  });

  const isFirstRender = useRef(true);

  const [refreshKey, setRefreshKey] = useState<number>(0);
  const {
    data: ingestionData,
    isLoading: isIngestionDataLoading,
    isError: isIngestionDataError,
  } = useGetIngestionData(refreshKey);
  // Dropdown options
  const sortOptions = ingestionData?.sortFields ?? [];
  const statusOptions = ingestionData?.ingestionStateList ?? [];
  const showApoUpdates = ingestionData != null && ingestionData.showApoUpdates;

  const defaultRequest: IngestionListRequest = {
    filterBy: 'INPROGRESS',
    sortBy: 'ModifiedDate',
    SortingOrder: 'desc',
    pageSize: 12,
    pageNumber: 1,
    ingestionNameSearchTerm: '',
  };
  const handleApplyFilters = (
    sortByingestion: ISelectItem | null,
    filterByingestion: ISelectItem[],
    showAll: boolean,
    ingestionNameSearchTerm: string
  ) => {
    setShowAllState(showAll); // Store it in state
    let totalRows = ingestionListData?.ingestionHomeModel?.totalRows;
    // Check for null, undefined, or 0
    if (!totalRows) {
      totalRows = 12;
    }

    setAppliedFilters({
      filterBy: filterByingestion.map((item) => item.id).join(','),
      sortBy: sortByingestion?.id?.toString() ?? '',
      SortingOrder: 'desc',
      pageSize: showAll ? totalRows : DEFAULT_PAGE_SIZE,
      pageNumber: 1,
      ingestionNameSearchTerm: ingestionNameSearchTerm,
    });
  };

  const finalRequest = isFirstRender.current ? defaultRequest : AppliedFilters;

  const {data: ingestionListData, refetch: refetchIngestionList} =
    useGetIngestionListData(finalRequest);
  // Summary Stats
  const summaryStats: SummaryStats = useMemo(() => {
    return {
      mappedIngests: ingestionListData?.ingestionHomeModel?.totalRows ?? 0,
      uniqueIngests:
        ingestionListData?.ingestionHomeModel?.totalNumberDocuments ?? 0,
    };
  }, [ingestionListData]);

  const defaultSortOption: ISelectItem = useMemo(
    () => ({
      value: 'IngestionDate',
      label: 'Last Modified Date',
    }),
    []
  );

  const defaultStatusOptions: ISelectItem[] = useMemo(
    () => [{value: 'INPROGRESS', label: 'In Progress'}],
    []
  );

  const [showToastNotification, setShowToastNotification] =
    useState<boolean>(false);

  const [toastNotificationMessage, setToastNotificationMessage] = useState('');
  const [toastNotificationType, setToastNotificationType] = useState<any>('');

  // Tiles
  const tileData = useMemo(() => {
    return ingestionListData?.ingestionHomeModel?.ingestionProgress ?? [];
  }, [ingestionListData]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClearAll = (key: boolean) => {
    refetchIngestionList(); // refresh with SAME filters
    setRefreshKey(1);
  };
  const {refetch} = useIngestionSummary({enabled: false});

  const onExportIngestions = async () => {
    const {error} = await refetch();
    setShowToastNotification(true);
    if (error) {
      setToastNotificationMessage(t('ingestions.exportErrorMessage'));
      setToastNotificationType(ToastType.Error);
    } else {
      setToastNotificationMessage(t('ingestions.exportSuccessMessage'));
      setToastNotificationType(ToastType.Success);
    }
  };
  const totalCount = ingestionListData?.ingestionHomeModel?.totalRows;
  let ingestionContent;
  if (isIngestionDataLoading) {
    ingestionContent = (
      <div data-testid={`view-ingestion-loading`}>Loading...</div>
    );
  } else if (tileData.length === 0) {
    ingestionContent = (
      <Box className="Ingestion-empty-container">
        <NoDataIcon />
        <Typography
          className="typo-text headline3_size no-saved-search-text"
          tabIndex={0}
          data-testid={`view-ingestion-empty-ingestions-msg`}
        >
          {t('ingestions.emptyIngestionsMsg')}
        </Typography>
      </Box>
    );
  } else {
    ingestionContent = (
      <div className="Ingestion-widgets" data-testid={`view-ingestion-div`}>
        <IngestionTile
          items={tileData}
          initialCount={12}
          step={12}
          showAll={showAllState}
          totalCount={totalCount}
          dataTestId="view-ingestion"
        />
      </div>
    );
  }
  return (
    <Box className="view-ingestion">
      <IngestionHeader
        onClearAll={handleClearAll}
        handleExport={onExportIngestions}
      />
      <IngestionSummary stats={summaryStats} showApoUpdates={showApoUpdates} />
      {!isIngestionDataLoading && !isIngestionDataError && ingestionData ? (
        <IngestionFilter
          options={sortOptions}
          statusOptions={statusOptions}
          defaultSingleValue={defaultSortOption}
          defaultMultiValues={defaultStatusOptions}
          onApplyFilters={handleApplyFilters}
        />
      ) : (
        <Box className="ingestion-loader-container">
          <ArrLoadingSpinner />
        </Box>
      )}
      {ingestionContent}
      {showToastNotification && (
        <ArrToastNotification
          message={toastNotificationMessage}
          status={[toastNotificationType]}
          onClose={() => setShowToastNotification(false)}
        />
      )}
    </Box>
  );
}

export default ViewIngestion;
